/**
 * Identidade visual do cliente da plataforma (nome, logos, cores) e o modo sol/lua.
 * O tema do cliente só troca variáveis CSS: o visual base é o mesmo para todos.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loading } from '@dotapps/ui';
import { PRODUCT_NAME, type PublicTenant } from '@dotapps/core';
import { api, assetUrl } from '../lib/api';

type Mode = 'dark' | 'light';

interface TenantCtx {
  tenant: PublicTenant;
  mode: Mode;
  toggleMode: () => void;
  /** logo certo para o modo atual */
  logo: string | null;
  mark: string | null;
}

const Ctx = createContext<TenantCtx | null>(null);
const MODE_KEY = 'dotapps-theme';

function currentMode(): Mode {
  const d = document.documentElement.dataset.theme;
  if (d === 'light' || d === 'dark') return d;
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** Injeta as variáveis de cor do cliente (modo lua e modo sol). */
function applyTheme(t: PublicTenant) {
  const css = (vars: Record<string, string>) =>
    Object.entries(vars)
      .filter(([k]) => /^--[\w-]+$/.test(k))
      .map(([k, v]) => `${k}:${String(v).replace(/[;{}<>]/g, '')};`)
      .join('');
  let el = document.getElementById('tenant-theme') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'tenant-theme';
    document.head.appendChild(el);
  }
  el.textContent = `:root{${css(t.theme.dark ?? {})}}:root[data-theme="light"]{${css(t.theme.light ?? {})}}`;
  document.title = `${t.name} · ${PRODUCT_NAME}`;
  const icon = assetUrl(t.logos.markOnDark);
  if (icon) document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.setAttribute('href', icon);
  const color = t.theme.dark?.['--theme-color'];
  if (color) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ['public-tenant'],
    queryFn: () => api.get<PublicTenant>('/public/tenant'),
    staleTime: Infinity,
    retry: 4,
  });
  const [mode, setMode] = useState<Mode>(currentMode);

  useEffect(() => {
    if (q.data) applyTheme(q.data);
  }, [q.data]);

  const toggleMode = useCallback(() => {
    setMode(m => {
      const next = m === 'dark' ? 'light' : 'dark';
      document.documentElement.dataset.theme = next;
      try {
        localStorage.setItem(MODE_KEY, next);
      } catch {
        /* ignora */
      }
      return next;
    });
  }, []);

  if (q.isError) {
    return (
      <div className="public-page">
        <div className="error-box">
          <b>Não foi possível abrir a plataforma.</b>
          <span className="muted">{(q.error as Error).message}</span>
          <button className="btn ghost" onClick={() => q.refetch()}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }
  if (!q.data)
    return <Loading label="Abrindo a plataforma… (o servidor gratuito pode levar até 1 minuto para acordar)" />;

  const t = q.data;
  const dark = mode === 'dark';
  const value: TenantCtx = {
    tenant: t,
    mode,
    toggleMode,
    logo: assetUrl(dark ? t.logos.onDark : (t.logos.onLight ?? t.logos.onDark)),
    mark: assetUrl(dark ? t.logos.markOnDark : (t.logos.markOnLight ?? t.logos.markOnDark)),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTenant(): TenantCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTenant fora do TenantProvider');
  return c;
}
